import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { useIntl } from 'react-intl';
import { IoMdArrowBack, IoMdArrowForward } from 'react-icons/io';
import { searchCriteria } from '~containers/workers/list/searchCriteria';
import { useGet } from '~hooks/useQuery';
import { isVerified } from '~helpers/common';
import { Suspense } from '~components/suspense';
import CarouselSkeleton from '~components/carousel/skeleton';
import ResultsItem from '~containers/workers/list/results-item';
import './style.scss';

// Helper function to safely parse JSON strings
const safeJsonParse = (jsonString) => {
  if (!jsonString) return null;
  try {
    return typeof jsonString === 'object' ? jsonString : JSON.parse(jsonString);
  } catch (e) {
    console.error('Error parsing JSON:', e);
    return null;
  }
};

// Worker card content component
const WorkerCardContent = ({ alternativeWorkers }) => {
  const intl = useIntl();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: false,
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
  });

  const [prevEnabled, setPrevEnabled] = useState(false);
  const [nextEnabled, setNextEnabled] = useState(false);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setPrevEnabled(emblaApi.canScrollPrev());
    setNextEnabled(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <>
      {alternativeWorkers.length > 1 && (
        <>
          <div className="alternatives">
            <h3 className="c-slider_title">
              {intl.formatMessage({ id: 'components.worker-card-carousel.title', defaultMessage: 'Alternative' })}
            </h3>
            <div className="c-new-cards">
              <div className="c_slider">
                <div className="c_slider__viewport" ref={emblaRef}>
                  <div className="c_slider__container">
                    {alternativeWorkers.map((worker) => (
                      <div className="c_slider__slide" key={worker.personId}>
                        <ResultsItem
                          key={worker.personId}
                          locationId={false}
                          personId={worker.personId}
                          score={worker.score}
                          price={worker.price}
                          address={worker.location}
                          experience={worker.experience}
                          firstName={worker.firstName}
                          profilePicture={worker.profilePicture}
                          verification={isVerified(worker.verification)}
                          lastLogin={worker.lastLogin}
                          isOnline={worker.isOnline}
                          personalNote={worker.personalNote}
                          registerDate={worker.registerDate}
                          days={worker.days}
                          published={worker.published}
                          isCityPage={false}
                          cityName={false}
                          citySlug={false}
                          slug={worker.slug}
                          showFavorite={false}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {alternativeWorkers.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="c_slider__arrow c_slider__arrow--prev"
                      onClick={scrollPrev}
                      disabled={!prevEnabled}
                      aria-label={intl.formatMessage({ id: 'components.worker-card-carousel.previous', defaultMessage: 'Previous' })}
                    >
                      <IoMdArrowBack />
                    </button>
                    <button
                      type="button"
                      className="c_slider__arrow c_slider__arrow--next"
                      onClick={scrollNext}
                      disabled={!nextEnabled}
                      aria-label={intl.formatMessage({ id: 'components.worker-card-carousel.next', defaultMessage: 'Next' })}
                    >
                      <IoMdArrowForward />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          
        </>
      )}    
    </>
  );
};

// Main component
const WorkerCardCarousel = ({
  workerLocation,
  workerPrice,
  currentWorkerId,
  detailedLocation,
  isLocationLoading = false,
  // cacheData = null,
}) => {
  const intl = useIntl();
  const [alternativeWorkers, setAlternativeWorkers] = useState([]);
  const [error, setError] = useState(null);

  // console.log(cacheData)

  // Parse worker data for API
  const parseWorkerData = (workerData) => {
    if (!workerData) return null;

    return {
      personId: workerData.id,
      firstName: workerData.first_name,
      profilePicture: safeJsonParse(workerData.profile_picture),
      lastLogin: workerData.last_login,
      registerDate: workerData.registered_date,
      isOnline: workerData.is_online,
      personalNote: workerData.personal_note,
      verification: workerData.verification
        ? workerData.verification.map((v) => ({
            type: v.type.toLowerCase(),
            isVerified: v.is_verified,
          }))
        : [],
      score: workerData.score,
      price: workerData.price,
      location: {
        place_id: workerData.location?.place_id,
        city: workerData.location?.city,
        country: workerData.location?.country,
        latitude: workerData.location?.latitude,
        longitude: workerData.location?.longitude,
      },
      experience: workerData.experience,
      days: workerData.days,
      published: workerData.published,
      slug: workerData.slug,
    };
  };

  // Changed: Use useGet hook instead of direct REST.get call
  const criteria = detailedLocation
    ? searchCriteria({
        location: detailedLocation.location,
        filters: {
          price: detailedLocation.pricing?.max_price,
        },
        currency: workerPrice?.currency || detailedLocation.pricing?.currency,
        pagination: {
          limit: 11, // Get 11 workers to ensure we have enough after filtering
          offset: 0,
        },
        order: {
          direction: 'desc',
          value: 'internal-score',
        },
      })
    : null;

  // Using useGet hook with cacheData, just like the review carousel
  const {
    data: workersResponse,
    isLoading: isWorkersLoading,
    isError,
  } = useGet(
    {
      name: 'workers',
      params: criteria,
      shouldExecute: !!detailedLocation,
      // cacheData: cacheData,
    },
    [detailedLocation, currentWorkerId]
  );

  // Process the workers data when it arrives
  useEffect(() => {
    if (workersResponse && workersResponse.data) {
      // Filter out the current worker from results
      const filteredResults = workersResponse.data
        .filter((w) => w.id !== currentWorkerId)
        .map(parseWorkerData)
        .slice(0, 10); // Limit to 10 alternatives for carousel

      setAlternativeWorkers(filteredResults);
    }
  }, [workersResponse, currentWorkerId]);

  // Set error state if request fails
  useEffect(() => {
    if (isError) {
      setError('Failed to load alternative workers');
    }
  }, [isError]);

  if (error) {
    return <div className="c_slider error">Error: {error}</div>;
  }

  // Use isWorkersLoading state
  const loadingState = isWorkersLoading;

  return (
    <Suspense fallback={<CarouselSkeleton />} isLoading={loadingState}>
      <WorkerCardContent alternativeWorkers={alternativeWorkers} />
    </Suspense>
  );
};

export default WorkerCardCarousel;
